import { useEffect, useRef, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";
import { Button, Form, Input } from "antd";
import { t } from "i18next";
import PropTypes from "prop-types";

import useCreateZone from "../../../../../api/hooks/useCreateZone";
import useDetailsByDeviceId from "../../../../../api/hooks/useDetailsByDeviceId";
import useSystemsByPropertyId from "../../../../../api/hooks/useSystemsByPropertyId";
import useZoneEdit from "../../../../../api/hooks/useZoneEdit";
import useZoneManage from "../../../../../api/hooks/useZoneManage";
import { updateTransferList } from "../helperFuncs/helperFunctions";
import useHandlePropertyZoneUpdate from "../hooks/useHandlePropertyZoneUpdate";
import ZoneSystems from "./ZoneSystems";
import "../scss/ZoneEdit.scss";

function ZoneCreate({
  propertyId,
  setIsFormDirty,
  setEditFloorFormReference,
  currentZone,
  setIsNewZoneModalOpen,
  setCurrentZone,
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [disableSaveChanges, setDisableSaveChanges] = useState(true);
  const [editFormFloor] = Form.useForm();
  const floorSystemsRef = useRef();
  const initialFormValues = useRef({});
  const queryClient = useQueryClient();

  const { data } = useSystemsByPropertyId(
    Number(propertyId),
    currentZone?.ZoneUniqueId ?? null,
    {
      enabled: true,
    },
  );

  const { data: zoneDetails } = useDetailsByDeviceId(currentZone?.ZoneUniqueId);

  const createZoneMutation = useCreateZone();
  const editZoneMutation = useZoneEdit();
  const manageZoneMutation = useZoneManage();

  useEffect(() => {
    if (!data) return;

    const zoneName = zoneDetails?.Device?.Device?.Name ?? "";
    const serialNumber = zoneDetails?.Device?.Device?.SerialNumber ?? null;

    const assigned = data.AssignedSystems ?? [];
    const unassigned = data.UnassignedSystems ?? [];

    const assignedFiltered = assigned
      .filter((s) => s.Devices.some((d) => d.ZoneSerialNumber === serialNumber))
      .map((s) => ({
        ...s,
        Devices: s.Devices.filter((d) => d.ZoneSerialNumber === serialNumber),
      }));

    const unassignedFiltered = unassigned
      .filter((s) => s.Devices.some((d) => d.ZoneSerialNumber === null))
      .map((s) => ({
        ...s,
        Devices: s.Devices.filter((d) => d.ZoneSerialNumber === null),
      }));

    initialFormValues.current = {
      ZoneName: zoneName,
      Systems: serialNumber
        ? {
            AssignedSystems: assignedFiltered,
            UnassignedSystems: unassignedFiltered,
          }
        : {
            AssignedSystems: assigned,
            UnassignedSystems: unassigned,
          },
    };

    editFormFloor.setFieldsValue(initialFormValues.current);
  }, [data, zoneDetails, editFormFloor]);

  useEffect(() => {
    setEditFloorFormReference(editFormFloor);
  }, [editFormFloor, setEditFloorFormReference]);

  const handleValuesChanges = (changedValues) => {
    if (Object.keys(changedValues).length > 0) {
      setIsFormDirty(true);
      setDisableSaveChanges(false);
    }
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    const formValues = editFormFloor.getFieldsValue();

    // Call your custom hook function correctly
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { createZoneResponse } = await useHandlePropertyZoneUpdate(
      formValues,
      createZoneMutation,
      editZoneMutation,
      manageZoneMutation,
      zoneDetails?.Device?.Device?.SerialNumber ?? null,
      setIsFormDirty,
      setIsLoading,
    );

    const updated = updateTransferList(createZoneResponse);

    editFormFloor.setFieldsValue({
      ZoneName: createZoneResponse?.data?.ZoneName ?? null,
      Systems: {
        AssignedSystems: updated.assignedSystems,
        UnassignedSystems: updated.unassignedSystems,
      },
    });

    // Update Transfer UI if floorSystemsRef is set
    if (floorSystemsRef.current) {
      floorSystemsRef.current.setDataSource(updated.allSystems);
      floorSystemsRef.current.setExpandedRowKeys(updated.allRowKeys);
      floorSystemsRef.current.setTargetKeys(updated.flattenTargetKeys);
      floorSystemsRef.current.setLeftTableData(updated.assignedSystems);
      floorSystemsRef.current.setRightTableData(updated.unassignedSystems);
    }

    await queryClient.invalidateQueries({ queryKey: ["getZonesByPropertyId"] });

    setDisableSaveChanges(true);
    setCurrentZone(null);
    setIsNewZoneModalOpen(false);
  };

  return (
    <div className="content-wrapper mx-2">
      <Form
        form={editFormFloor}
        layout="vertical"
        className="w-full"
        onFinish={handleSubmit}
        onValuesChange={handleValuesChanges}
      >
        <div className="flex items-center justify-center mb-0">
          <h1 className="page-title">
            {zoneDetails?.Device?.Device
              ? `${t("create_zone.edit_zone")} ${
                  zoneDetails?.Device?.Device?.SerialNumber
                }`
              : t("create_zone.create_zone")}
          </h1>
        </div>

        <Form.Item name={["Systems", "AssignedSystems"]} hidden>
          <Input />
        </Form.Item>
        <Form.Item name={["Systems", "UnassignedSystems"]} hidden>
          <Input />
        </Form.Item>

        <Form.Item
          name="ZoneName"
          label="Zone Name"
          rules={[{ required: true, message: "Zone Name is required" }]}
        >
          <Input placeholder="Zone Name" />
        </Form.Item>

        {Number.isFinite(propertyId) && (
          <ZoneSystems
            editFloorFormReference={editFormFloor}
            propertyId={Number(propertyId)}
            setDisableSaveChanges={setDisableSaveChanges}
            setIsFormDirty={setIsFormDirty}
            floorSystemsRef={floorSystemsRef}
            zoneSerialNumber={zoneDetails?.Device?.Device?.SerialNumber ?? null}
          />
        )}

        <Button
          type="primary"
          htmlType="submit"
          size="medium"
          className="custom-property-save-btn"
          loading={isLoading}
          disabled={disableSaveChanges}
        >
          {t("create_zone.save")}
        </Button>
      </Form>
    </div>
  );
}

ZoneCreate.propTypes = {
  propertyId: PropTypes.number.isRequired,
  setIsFormDirty: PropTypes.func,
  setEditFloorFormReference: PropTypes.func,
  currentZone: PropTypes.any,
  setIsNewZoneModalOpen: PropTypes.func,
  setCurrentZone: PropTypes.func,
};

ZoneCreate.defaultProps = {
  setIsFormDirty: () => {},
  setEditFloorFormReference: () => {},
  currentZone: null,
  setIsNewZoneModalOpen: () => {},
  setCurrentZone: () => {},
};

export default ZoneCreate;
